import { RevaluationPrefix } from 'further-types/lp/revaluation';
import { useState } from 'react';
import { UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { RevaluationForm } from '../../types';
import {
  updateAllNewRevaluationsDate,
  updateAllNewRevaluationsUnitPrice,
} from './newRevaluationBulkUpdates';

type RevaluationHookProps = {
  watch: UseFormWatch<RevaluationForm>;
  setValue: UseFormSetValue<RevaluationForm>;
};

type ActiveField = 'holdValue' | 'unitPriceTcy' | null;

const useRevaluationsFormHandlers = ({
  watch,
  setValue,
}: RevaluationHookProps) => {
  const [activeField, setActiveField] = useState<ActiveField>(null);

  const newRevaluationsWatch = watch('newRevaluations');
  const investmentHistory = watch('investmentHistory') ?? [];

  const handleCurrentHoldValueChange = (
    value: number,
    numberOfUnits: number,
    index: number,
    prefix: RevaluationPrefix,
  ) => {
    if (activeField === 'holdValue' && numberOfUnits && numberOfUnits > 0) {
      setValue(`${prefix}.${index}.unitPriceTcy`, value / numberOfUnits);
    }
  };

  const handleUnitPriceChange = (
    value: number,
    numberOfUnits: number,
    index: number,
    prefix: RevaluationPrefix,
  ) => {
    if (activeField === 'unitPriceTcy' && numberOfUnits && numberOfUnits > 0) {
      setValue(`${prefix}.${index}.currentHoldValue`, value * numberOfUnits);
    }
  };

  const handleOverallHoldValueChange = (value: number, totalUnits: number) => {
    if (totalUnits > 0 && activeField === 'holdValue') {
      const newUnitPrice = value / totalUnits;

      setValue('currentUnitPrice', newUnitPrice);

      updateAllNewRevaluationsUnitPrice(
        newUnitPrice,
        newRevaluationsWatch,
        investmentHistory,
        setValue,
      );
    }
  };

  const handleOverallUnitPriceChange = (value: number, totalUnits: number) => {
    if (totalUnits > 0 && activeField === 'unitPriceTcy') {
      const newHoldValue = value * totalUnits;

      setValue('currentHoldValue', newHoldValue);
      updateAllNewRevaluationsUnitPrice(
        value,
        newRevaluationsWatch,
        investmentHistory,
        setValue,
      );
    }
  };

  return {
    handleCurrentHoldValueChange,
    handleUnitPriceChange,
    setFieldFocus: (field: ActiveField) => setActiveField(field),
    updateAllNewRevaluationsDate: (date: Date) =>
      updateAllNewRevaluationsDate(date, newRevaluationsWatch, setValue),
    handleOverallHoldValueChange,
    handleOverallUnitPriceChange,
  };
};

export default useRevaluationsFormHandlers;
